import React, { useCallback, useContext, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import toast from "react-hot-toast";
import AccountabilitySettingsDialog from "../../sales/pipeline/components/AccountabilitySettingsDialog";
import {
  authorizeMicrosoftCrmEmailAccount,
  connectImapCrmEmailAccount,
  createCrmEmailAccount,
  createCrmTag,
  disconnectCrmEmailAccount,
  fetchCrmEmailAccounts,
  fetchCrmEmailRoutingSettings,
  fetchCrmTags,
  fetchCrmUnmappedCommunications,
  getFriendlyApiError,
  syncCrmEmailAccount,
  updateCrmEmailAccount,
  updateCrmEmailRoutingSettings,
  updateCrmTag,
} from "../../../../services/crm";

import { AuthContext } from "../../../../helpers/AuthContext";
import { formatDateTimeInTimeZone } from "../../../../utils/timezone";

const DEFAULT_EMAIL_ROUTING_SETTINGS = {
  defaultCompanyMailboxAccountId: "",
  leadCaptureMailboxAccountId: "",
  ordersMailboxAccountId: "",
  allowUserMailboxConnections: true,
  userMailboxMode: "optional",
  managerVisibilityMode: "linked_threads_only",
  privateEmailProtection: true,
  syncScope: "tracking_refs_only",
  manualReviewFallback: true,
  trackingReferenceEnabled: true,
  trackingReferencePrefix: "EL-CRM",
  autoCreateUnmappedItems: false,
  autoLinkConfidenceThreshold: "",
  externalDeliveryEnabled: false,
};

const USER_MAILBOX_MODE_LABELS = {
  disabled: "Disabled",
  optional: "Optional",
  required_for_reps: "Required for reps",
};

const MANAGER_VISIBILITY_LABELS = {
  linked_threads_only: "Linked threads only",
  all_crm_matched_threads: "All CRM-matched threads",
  manager_visible_after_link: "Manager visible after link",
};

const SYNC_SCOPE_LABELS = {
  tracking_refs_only: "Tracking references only",
  known_contacts_only: "Known CRM contacts only",
  linked_threads_only: "Linked threads only",
  tracking_refs_and_known_contacts: "Tracking references + known contacts",
};

const DEFAULT_TRACKING_EXAMPLES = {
  prospect: "[EL-CRM-PROSPECT-123]",
  order: "[EL-CRM-ORDER-987]",
  card: "[EL-CRM-CARD-456]",
};

function SettingsGroup({ title, description, children }) {
  return (
    <Card variant="outlined" sx={{ borderColor: "divider", boxShadow: "none" }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>{description}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function FutureFeatureCard({ title, description, items = [], children, chipLabel = "Future", chipColor = "primary" }) {
  return (
    <Card variant="outlined" sx={{ height: "100%", borderColor: "divider", boxShadow: "none", backgroundColor: "#fafafa" }}>
      <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          <Chip size="small" variant="outlined" color={chipColor} label={chipLabel} />
        </Stack>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
        <Stack spacing={0.6} sx={{ flex: 1 }}>
          {items.map((item) => (
            <Stack key={item} direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "text.disabled", flexShrink: 0 }} />
              <Typography variant="body2">{item}</Typography>
            </Stack>
          ))}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

export default function CrmSettingsPanel({ instanceUniqueId, canManage = false }) {
  const { activeInstanceTimezone } = useContext(AuthContext);
  const [accountabilityOpen, setAccountabilityOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#64748b");
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [unmappedEmails, setUnmappedEmails] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSavingId, setEmailSavingId] = useState(null);
  const [newMailbox, setNewMailbox] = useState({ provider: "microsoft365", emailAddress: "", displayName: "", syncInboxEnabled: false, syncSentEnabled: false, syncOutboxEnabled: false, autoSyncEnabled: false, autoSyncIntervalMinutes: 15, imap: { host: "", port: 993, username: "", security: "tls" } });
  const [imapPasswords, setImapPasswords] = useState({});
  const [routingSettings, setRoutingSettings] = useState(DEFAULT_EMAIL_ROUTING_SETTINGS);
  const [trackingExamples, setTrackingExamples] = useState(DEFAULT_TRACKING_EXAMPLES);
  const [routingSaving, setRoutingSaving] = useState(false);
  const formatInstanceDateTime = (value) =>
    formatDateTimeInTimeZone(value, { timeZone: activeInstanceTimezone }) || "-";

  const loadTags = useCallback(async () => {
    if (!instanceUniqueId || !canManage) return;
    try {
      setLoading(true);
      const result = await fetchCrmTags(instanceUniqueId, { includeInactive: true });
      setTags(result.tags || []);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not load CRM tags."));
    } finally {
      setLoading(false);
    }
  }, [canManage, instanceUniqueId]);

  useEffect(() => { loadTags(); }, [loadTags]);

  const loadEmailFoundation = useCallback(async () => {
    if (!instanceUniqueId || !canManage) return;
    try {
      setEmailLoading(true);
      const [accountsResult, queueResult, routingResult] = await Promise.all([
        fetchCrmEmailAccounts(instanceUniqueId),
        fetchCrmUnmappedCommunications(instanceUniqueId),
        fetchCrmEmailRoutingSettings(instanceUniqueId),
      ]);
      setEmailAccounts(accountsResult.emailAccounts || []);
      setUnmappedEmails(queueResult.communications || []);
      setRoutingSettings({ ...DEFAULT_EMAIL_ROUTING_SETTINGS, ...(routingResult.settings || {}) });
      setTrackingExamples(routingResult.trackingReferenceExamples || DEFAULT_TRACKING_EXAMPLES);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not load CRM email settings."));
    } finally { setEmailLoading(false); }
  }, [canManage, instanceUniqueId]);

  useEffect(() => { loadEmailFoundation(); }, [loadEmailFoundation]);

  const addCompanyMailbox = async () => {
    try {
      setEmailSavingId("new");
      await createCrmEmailAccount(instanceUniqueId, { ...newMailbox, scope: "company" });
      setNewMailbox({ provider: "microsoft365", emailAddress: "", displayName: "", syncInboxEnabled: false, syncSentEnabled: false, syncOutboxEnabled: false, autoSyncEnabled: false, autoSyncIntervalMinutes: 15, imap: { host: "", port: 993, username: "", security: "tls" } });
      await loadEmailFoundation();
      toast.success("Company mailbox placeholder saved.");
    } catch (error) { toast.error(getFriendlyApiError(error, "Could not save company mailbox.")); }
    finally { setEmailSavingId(null); }
  };

  const patchEmailAccount = (id, changes) => setEmailAccounts((current) => current.map((account) => Number(account.id) === Number(id) ? { ...account, ...changes } : account));
  const patchRoutingSetting = (field, value) => setRoutingSettings((current) => ({ ...current, [field]: value }));
  const companyMailboxOptions = emailAccounts.filter((account) => account.scope === "company");
  const providerLabel = (provider) => ({
    microsoft365: "Microsoft 365",
    generic_secure_imap: "Secure IMAP",
    google: "Google",
    imap: "Secure IMAP",
    manual: "Manual",
  }[String(provider || "").toLowerCase()] || String(provider || "Mailbox"));
  const statusLabel = (value) => String(value || "not_connected").replaceAll("_", " ");
  const derivedStateLabel = (account) => ({
    not_connected: "Not connected",
    connected: "Connected",
    syncing: "Syncing",
    connected_with_warning: "Connected with warning",
    reauthorization_required: "Reauthorization required",
    failed: "Failed",
    disconnected: "Disconnected",
  }[account.derivedState || account.status] || statusLabel(account.derivedState || account.status));
  const syncBlocked = (account) => emailSavingId === `sync-${account.id}` || account.syncStatus === "running";

  const saveRoutingSettings = async () => {
    try {
      setRoutingSaving(true);
      const result = await updateCrmEmailRoutingSettings(instanceUniqueId, routingSettings);
      setRoutingSettings({ ...DEFAULT_EMAIL_ROUTING_SETTINGS, ...(result.settings || {}) });
      setTrackingExamples(result.trackingReferenceExamples || trackingExamples);
      toast.success("Email routing settings saved.");
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not save email routing settings."));
    } finally {
      setRoutingSaving(false);
    }
  };

  const saveEmailAccount = async (account) => {
    try {
      setEmailSavingId(account.id);
      await updateCrmEmailAccount(instanceUniqueId, account.id, account);
      await loadEmailFoundation();
      toast.success("Mailbox placeholder updated.");
    } catch (error) { toast.error(getFriendlyApiError(error, "Could not update mailbox.")); }
    finally { setEmailSavingId(null); }
  };

  const connectMicrosoft = async (account) => {
    try {
      setEmailSavingId(`connect-${account.id}`);
      const result = await authorizeMicrosoftCrmEmailAccount(instanceUniqueId, account.id);
      if (result.authorizationUrl) window.location.assign(result.authorizationUrl);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not start Microsoft 365 connection."));
    } finally {
      setEmailSavingId(null);
    }
  };

  const disconnectMailbox = async (account) => {
    if (!window.confirm("Disconnect this mailbox from CRM? Existing CRM communications and review items will remain.")) return;
    try {
      setEmailSavingId(`disconnect-${account.id}`);
      await disconnectCrmEmailAccount(instanceUniqueId, account.id);
      await loadEmailFoundation();
      toast.success("Mailbox disconnected.");
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not disconnect mailbox."));
    } finally {
      setEmailSavingId(null);
    }
  };

  const patchNewImap = (changes) => setNewMailbox((current) => ({ ...current, imap: { ...(current.imap || {}), ...changes } }));
  const patchAccountImap = (account, changes) => patchEmailAccount(account.id, { metadata: { ...(account.metadata || {}), imap: { ...(account.imap || account.metadata?.imap || {}), ...changes } }, imap: { ...(account.imap || account.metadata?.imap || {}), ...changes } });
  const isImapProvider = (provider) => ["imap", "generic_secure_imap"].includes(String(provider || "").toLowerCase());

  const connectImap = async (account) => {
    try {
      setEmailSavingId(`connect-imap-${account.id}`);
      const imap = account.imap || account.metadata?.imap || {};
      await connectImapCrmEmailAccount(instanceUniqueId, account.id, {
        imap,
        password: imapPasswords[account.id] || "",
      });
      await loadEmailFoundation();
      toast.success("Secure IMAP mailbox connected.");
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not connect secure IMAP mailbox."));
    } finally {
      setImapPasswords((current) => ({ ...current, [account.id]: "" }));
      setEmailSavingId(null);
    }
  };

  const syncMailbox = async (account) => {
    try {
      setEmailSavingId(`sync-${account.id}`);
      const result = await syncCrmEmailAccount(instanceUniqueId, account.id);
      await loadEmailFoundation();
      toast.success(`Sync complete: ${result.summary?.linked || 0} linked, ${result.summary?.queued || 0} queued for review.`);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not sync mailbox."));
    } finally {
      setEmailSavingId(null);
    }
  };

  const patchLocalTag = (id, changes) => setTags((current) => current.map((tag) => Number(tag.id) === Number(id) ? { ...tag, ...changes } : tag));

  const saveTag = async (tag, override = {}) => {
    try {
      setSavingId(tag.id);
      const result = await updateCrmTag(instanceUniqueId, tag.id, {
        name: tag.name,
        color: tag.color,
        isActive: tag.isActive !== false,
        ...override,
      });
      patchLocalTag(tag.id, result.tag);
      toast.success("CRM tag updated.");
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not update CRM tag."));
      await loadTags();
    } finally {
      setSavingId(null);
    }
  };

  const addTag = async () => {
    if (!newName.trim()) return;
    try {
      setSavingId("new");
      const result = await createCrmTag(instanceUniqueId, { name: newName, color: newColor });
      setNewName("");
      setTags((current) => [...current.filter((tag) => Number(tag.id) !== Number(result.tag.id)), result.tag].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("CRM tag created.");
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not create CRM tag."));
    } finally {
      setSavingId(null);
    }
  };

  if (!canManage) return <Alert severity="warning">CRM settings are available to managers and administrators.</Alert>;

  return (
    <Stack spacing={2}>
      <SettingsGroup title="Accountability Settings" description="Configure stage aging, next-activity requirements, estimate aging, and policy defaults.">
        <Button variant="contained" onClick={() => setAccountabilityOpen(true)}>Open Accountability Settings</Button>
      </SettingsGroup>

      <SettingsGroup title="Tag Management" description="Manage the shared CRM tag master. Active tags can be assigned from prospect and opportunity workspaces.">
        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
          <TextField size="small" label="Tag name" value={newName} onChange={(event) => setNewName(event.target.value)} inputProps={{ maxLength: 80 }} />
          <TextField size="small" type="color" label="Color" value={newColor} onChange={(event) => setNewColor(event.target.value)} sx={{ width: 100 }} />
          <Button variant="contained" onClick={addTag} disabled={!newName.trim() || savingId === "new"}>Create Tag</Button>
        </Stack>
        {loading ? <CircularProgress size={24} /> : (
          <Stack spacing={1}>
            {tags.map((tag) => (
              <Stack key={tag.id} direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ xs: "stretch", md: "center" }} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1, opacity: tag.isActive === false ? 0.65 : 1 }}>
                <Chip label={tag.isActive === false ? "Inactive" : "Active"} size="small" color={tag.isActive === false ? "default" : "success"} />
                <TextField size="small" label="Name" value={tag.name} onChange={(event) => patchLocalTag(tag.id, { name: event.target.value })} sx={{ minWidth: 220 }} />
                <TextField size="small" type="color" label="Color" value={tag.color} onChange={(event) => patchLocalTag(tag.id, { color: event.target.value })} sx={{ width: 100 }} />
                <Button variant="outlined" onClick={() => saveTag(tag)} disabled={savingId === tag.id}>Save</Button>
                <Button color={tag.isActive === false ? "success" : "warning"} onClick={() => saveTag(tag, { isActive: tag.isActive === false })} disabled={savingId === tag.id}>{tag.isActive === false ? "Activate" : "Deactivate"}</Button>
              </Stack>
            ))}
            {!tags.length ? <Alert severity="info">No CRM tags have been created.</Alert> : null}
          </Stack>
        )}
      </SettingsGroup>

      <SettingsGroup title="Reports / Management Visibility" description="Operational reports and Needs Attention views remain in CRM Pipeline, where they use the current pipeline scope and board data.">
        <Alert severity="info">CRM Settings configures behavior; CRM Pipeline remains the management reporting workspace.</Alert>
      </SettingsGroup>

      <SettingsGroup title="Notification Rules / Delivery" description="CRM accountability alerts use Elite's existing notification system. External delivery remains disabled.">
        <Stack spacing={1.25}>
          <Alert severity="success">In-app CRM alerts can be generated on demand by managers from CRM Pipeline. Reps receive only alerts assigned to their user in this company instance.</Alert>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
            <FutureFeatureCard chipLabel="In-app" chipColor="success" title="Active in-app alert types" description="Generated alerts are policy-based and deduplicated before they appear in the existing notification bell." items={["Activity due soon", "Activity overdue", "No next activity", "Stage aging", "Estimate aging", "Manager escalation candidate"]} />
            <FutureFeatureCard title="Future delivery channels" description="These channels are shown for planning only and are not enabled." items={["Email digest", "Manager digest", "Teams / Slack", "Email reply received"]} />
          </Box>
          <FutureFeatureCard title="Notification chip colors" description="The bell uses fixed source colors now. Per-company color preferences need a small CRM UI-preferences API before they are editable here." items={["Fixed palette: CRM, Chat, Mention, Order, Feed, System", "Future fixed choices: Red, Orange, Yellow, Green, Blue, Purple, Pink, Gray, Teal"]} />
        </Stack>
      </SettingsGroup>

      <SettingsGroup title="Email Routing & Mailbox Strategy" description="Configure the company mailbox strategy, user mailbox policy, visibility rules, and tracking reference format before provider sync is enabled.">
        <Stack spacing={1.5}>
          <Alert severity="info">Microsoft 365 connection is read-only. CRM imports relevant mailbox messages, private email protection remains active, unrelated mail is not automatically attached, and ambiguous messages go to manager review.</Alert>
          <Alert severity="warning">Email sending continues only through the existing Elite document-email workflows. CRM mailbox sync does not enable external sending.</Alert>
          {emailLoading ? <CircularProgress size={24} /> : null}

          <Typography variant="subtitle2" fontWeight={700}>Strategy overview</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
            <FutureFeatureCard chipLabel="Policy" chipColor="success" title="Shared company mailbox" description="Use sales@, crm@, or orders@ style inboxes for lead capture, controlled review, and future matching." items={["Team-visible CRM intake", "Manager-controlled unmapped review", "Shared ownership for routing"]} />
            <FutureFeatureCard chipLabel="Policy" chipColor="success" title="User mailboxes" description="Rep-owned mailboxes support natural follow-up while staying limited to CRM-linked or policy-matched threads." items={["Rep-owned conversations", "Private email protection", "Configurable manager visibility"]} />
            <FutureFeatureCard chipLabel="Fallback" chipColor="warning" title="Manual review" description="Unmatched CRM emails can stay in the unmapped review queue for manager resolution." items={["No automatic replies", "Manager-controlled review", "No automated mapping in this pass"]} />
          </Box>

          <Typography variant="subtitle2" fontWeight={700}>Company shared mailbox</Typography>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1} alignItems={{ lg: "center" }}>
            <TextField select SelectProps={{ native: true }} size="small" label="Provider" value={newMailbox.provider} onChange={(event) => setNewMailbox((current) => ({ ...current, provider: event.target.value }))}>
              <option value="microsoft365">Microsoft 365</option><option value="generic_secure_imap">Secure IMAP</option><option value="manual">Manual</option>
            </TextField>
            <TextField size="small" label="Email address" value={newMailbox.emailAddress} onChange={(event) => setNewMailbox((current) => ({ ...current, emailAddress: event.target.value }))} />
            <TextField size="small" label="Display name" value={newMailbox.displayName} onChange={(event) => setNewMailbox((current) => ({ ...current, displayName: event.target.value }))} />
            <Button variant="contained" disabled={!newMailbox.emailAddress.trim() || emailSavingId === "new"} onClick={addCompanyMailbox}>Add mailbox</Button>
          </Stack>
          {isImapProvider(newMailbox.provider) ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 1fr) 120px minmax(220px, 1fr)" }, gap: 1 }}>
              <TextField size="small" label="IMAP host" value={newMailbox.imap.host} onChange={(event) => patchNewImap({ host: event.target.value })} helperText="Secure IMAP TLS host" />
              <TextField size="small" label="Port" type="number" value={newMailbox.imap.port} onChange={(event) => patchNewImap({ port: Number(event.target.value) || 993 })} helperText="993 only" />
              <TextField size="small" label="Username" value={newMailbox.imap.username} onChange={(event) => patchNewImap({ username: event.target.value })} helperText="Usually the mailbox address" />
            </Box>
          ) : null}
          <Stack spacing={1}>
            {emailAccounts.filter((account) => account.scope === "company").map((account) => (
              <Card key={account.id} variant="outlined"><CardContent>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(240px, 1.2fr) minmax(220px, 0.9fr) minmax(280px, 1fr)" }, gap: 1.25, alignItems: "center" }}>
                  <Box>
                    <Typography fontWeight={600}>{account.displayName || account.emailAddress}</Typography>
                    <Typography variant="body2" color="text.secondary">{account.emailAddress}</Typography>
                    <Typography variant="caption" color="text.secondary">{providerLabel(account.provider)} &middot; Read-only mailbox access</Typography>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                      <Chip size="small" color={account.derivedState === "connected_with_warning" ? "warning" : (account.connectionStatus === "active" || account.status === "connected" ? "success" : "default")} label={derivedStateLabel(account)} />
                      <Chip size="small" variant="outlined" label={`Sync ${statusLabel(account.syncStatus || "idle")}`} />
                      {account.autoSyncEnabled ? <Chip size="small" color="info" variant="outlined" label={`Auto ${account.autoSyncIntervalMinutes || 15}m`} /> : null}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                    {[["syncInboxEnabled", "Inbox"], ["syncSentEnabled", "Sent"]].map(([field, label]) => <FormControlLabel key={field} control={<Checkbox checked={account[field] === true} disabled={emailSavingId === account.id} onChange={(event) => patchEmailAccount(account.id, { [field]: event.target.checked })} />} label={label} />)}
                    <FormControlLabel control={<Checkbox checked={account.syncOutboxEnabled === true} disabled />} label="Outbox unsupported" />
                    <FormControlLabel control={<Switch checked={account.autoSyncEnabled === true} disabled={emailSavingId === account.id} onChange={(event) => patchEmailAccount(account.id, { autoSyncEnabled: event.target.checked, metadata: { ...(account.metadata || {}), autoSyncEnabled: event.target.checked } })} />} label="Auto sync" />
                    <TextField size="small" type="number" label="Interval" value={account.autoSyncIntervalMinutes || 15} onChange={(event) => patchEmailAccount(account.id, { autoSyncIntervalMinutes: Number(event.target.value) || 15, metadata: { ...(account.metadata || {}), autoSyncIntervalMinutes: Number(event.target.value) || 15 } })} sx={{ width: 110 }} inputProps={{ min: 5, max: 1440 }} />
                  </Stack>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: "flex-start", lg: "flex-end" }}>
                    <Button variant="outlined" disabled={emailSavingId === account.id} onClick={() => saveEmailAccount(account)}>Save sync preferences</Button>
                    {account.provider === "microsoft365" ? <Button variant="contained" disabled={emailSavingId === `connect-${account.id}` || account.providerConnectionConfigured === false} onClick={() => connectMicrosoft(account)}>{account.status === "connected" ? "Reconnect Microsoft 365" : "Connect Microsoft 365"}</Button> : null}
                    {isImapProvider(account.provider) ? <Button variant="contained" disabled={emailSavingId === `connect-imap-${account.id}` || !imapPasswords[account.id]} onClick={() => connectImap(account)}>{account.status === "connected" ? "Reconnect IMAP" : "Connect IMAP"}</Button> : null}
                    <Button variant="outlined" disabled={syncBlocked(account) || account.status !== "connected" || account.credentialConfigured !== true || (account.syncInboxEnabled !== true && account.syncSentEnabled !== true)} onClick={() => syncMailbox(account)}>{account.syncStatus === "running" ? "Syncing..." : account.derivedState === "failed" ? "Retry failed sync" : "Sync Now"}</Button>
                    <Button color="warning" disabled={emailSavingId === `disconnect-${account.id}` || account.status !== "connected"} onClick={() => disconnectMailbox(account)}>Disconnect</Button>
                  </Stack>
                </Box>
                {account.provider === "microsoft365" && account.providerConnectionConfigured === false ? <Alert severity="warning" sx={{ mt: 1 }}>Microsoft 365 connection is not configured for this environment. Connect is disabled until setup is complete.</Alert> : null}
                {isImapProvider(account.provider) ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(220px, 1fr) 110px minmax(220px, 1fr) minmax(220px, 1fr)" }, gap: 1, mt: 1 }}>
                    <TextField size="small" label="IMAP host" value={account.imap?.host || ""} onChange={(event) => patchAccountImap(account, { host: event.target.value })} />
                    <TextField size="small" type="number" label="Port" value={account.imap?.port || 993} onChange={(event) => patchAccountImap(account, { port: Number(event.target.value) || 993 })} inputProps={{ min: 993, max: 993 }} />
                    <TextField size="small" label="Username" value={account.imap?.username || account.emailAddress || ""} onChange={(event) => patchAccountImap(account, { username: event.target.value })} />
                    <TextField size="small" type="password" label="App password" value={imapPasswords[account.id] || ""} onChange={(event) => setImapPasswords((current) => ({ ...current, [account.id]: event.target.value }))} autoComplete="new-password" />
                  </Box>
                ) : null}
                <Stack direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Inbox: {account.syncInboxEnabled ? "Enabled" : "Disabled"}</Typography>
                  <Typography variant="caption" color="text.secondary">Sent Items: {account.syncSentEnabled ? "Enabled" : "Disabled"}</Typography>
                  <Typography variant="caption" color="text.secondary">Automatic sync: {account.autoSyncEnabled ? `Enabled (${account.autoSyncIntervalMinutes || 15}m)` : "Disabled"}</Typography>
                  <Typography variant="caption" color="text.secondary">Last success: {formatInstanceDateTime(account.lastSuccessfulSyncAt)}</Typography>
                  <Typography variant="caption" color="text.secondary">Last attempt: {formatInstanceDateTime(account.lastAttemptedSyncAt)}</Typography>
                  {account.latestSyncError ? <Typography variant="caption" color="error">Latest error: {account.latestSyncError}</Typography> : null}
                </Stack>
                {Array.isArray(account.syncStates) && account.syncStates.length ? (
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {account.syncStates.map((state) => (
                      <Stack key={`${account.id}-${state.folderType}`} direction={{ xs: "column", md: "row" }} spacing={1} useFlexGap flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={`${state.folderType}: ${statusLabel(state.syncStatus)}`} />
                        <Typography variant="caption" color="text.secondary">Failures: {state.consecutiveFailureCount || 0}</Typography>
                        {state.metadata?.pendingContinuation ? <Typography variant="caption" color="warning.main">Continuation pending</Typography> : null}
                        {state.metadata?.cursorRecovery ? <Typography variant="caption" color="warning.main">Cursor recovery applied</Typography> : null}
                        {state.lastErrorMessage ? <Typography variant="caption" color="error">{state.lastErrorMessage}</Typography> : null}
                      </Stack>
                    ))}
                  </Stack>
                ) : null}
              </CardContent></Card>
            ))}
            {!emailLoading && !emailAccounts.some((account) => account.scope === "company") ? <Alert severity="info">No company mailbox placeholder configured.</Alert> : null}
          </Stack>

          <Typography variant="subtitle2" fontWeight={700}>Company mailbox selectors</Typography>
          {companyMailboxOptions.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
              {[
                ["defaultCompanyMailboxAccountId", "Default CRM mailbox"],
                ["leadCaptureMailboxAccountId", "Notifications mailbox"],
                ["ordersMailboxAccountId", "Orders/project mailbox"],
              ].map(([field, label]) => (
                <TextField key={field} select SelectProps={{ native: true, displayEmpty: true }} InputLabelProps={{ shrink: true }} size="small" label={label} value={routingSettings[field] || ""} onChange={(event) => patchRoutingSetting(field, event.target.value)} helperText="Optional" sx={{ minWidth: 0 }}>
                  <option value="">Not selected</option>
                  {companyMailboxOptions.map((account) => <option key={account.id} value={account.id}>{account.displayName || account.emailAddress} ({account.emailAddress})</option>)}
                </TextField>
              ))}
            </Box>
          ) : <Alert severity="info">Create a company mailbox placeholder before assigning default, lead capture, or orders/project mailbox roles.</Alert>}

          <Divider />

          <Typography variant="subtitle2" fontWeight={700}>User / rep mailboxes</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1 }}>
            <TextField select SelectProps={{ native: true }} size="small" label="User mailbox policy" value={routingSettings.userMailboxMode} onChange={(event) => patchRoutingSetting("userMailboxMode", event.target.value)}>
              {Object.entries(USER_MAILBOX_MODE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </TextField>
            <TextField select SelectProps={{ native: true }} size="small" label="Manager visibility" value={routingSettings.managerVisibilityMode} onChange={(event) => patchRoutingSetting("managerVisibilityMode", event.target.value)}>
              {Object.entries(MANAGER_VISIBILITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </TextField>
            <FormControlLabel control={<Switch checked={routingSettings.privateEmailProtection === true} onChange={(event) => patchRoutingSetting("privateEmailProtection", event.target.checked)} />} label="Private email protection" />
          </Box>
          <FormControlLabel control={<Switch checked={routingSettings.allowUserMailboxConnections === true} onChange={(event) => patchRoutingSetting("allowUserMailboxConnections", event.target.checked)} />} label="Allow user mailbox connections" />
          <Alert severity="info">User mailboxes can be connected and managed by CRM administrators. Private email protection and mailbox visibility rules remain active.</Alert>
          {emailAccounts.filter((account) => account.scope === "user").map((account) => <Typography key={account.id} variant="body2">{account.displayName || account.emailAddress} - {account.emailAddress} ({account.status})</Typography>)}
          {!emailAccounts.some((account) => account.scope === "user") ? <Typography variant="body2" color="text.secondary">No user mailboxes have been added for this CRM instance.</Typography> : null}

          <Typography variant="subtitle2" fontWeight={700}>Sync scope policy</Typography>
          <TextField select SelectProps={{ native: true }} size="small" label="Sync scope" value={routingSettings.syncScope} onChange={(event) => patchRoutingSetting("syncScope", event.target.value)} sx={{ maxWidth: 420 }}>
            {Object.entries(SYNC_SCOPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </TextField>

          <Typography variant="subtitle2" fontWeight={700}>Tracking reference settings</Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <FormControlLabel control={<Switch checked={routingSettings.trackingReferenceEnabled === true} onChange={(event) => patchRoutingSetting("trackingReferenceEnabled", event.target.checked)} />} label="Enable tracking references" />
            <TextField size="small" label="Prefix" value={routingSettings.trackingReferencePrefix || "EL-CRM"} onChange={(event) => patchRoutingSetting("trackingReferencePrefix", event.target.value.toUpperCase())} inputProps={{ maxLength: 31 }} />
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <Chip variant="outlined" label={trackingExamples.prospect || "[EL-CRM-PROSPECT-123]"} />
            <Chip variant="outlined" label={trackingExamples.order || "[EL-CRM-ORDER-987]"} />
            <Chip variant="outlined" label={trackingExamples.card || "[EL-CRM-CARD-456]"} />
          </Stack>

          <Typography variant="subtitle2" fontWeight={700}>Unmapped email review</Typography>
          <FormControlLabel control={<Switch checked={routingSettings.manualReviewFallback === true} onChange={(event) => patchRoutingSetting("manualReviewFallback", event.target.checked)} />} label="Use manual review fallback for unmatched CRM emails" />
          {unmappedEmails.length ? (
            <Table size="small"><TableHead><TableRow><TableCell>Received</TableCell><TableCell>From</TableCell><TableCell>Subject</TableCell><TableCell>Status</TableCell></TableRow></TableHead><TableBody>
              {unmappedEmails.map((email) => <TableRow key={email.id}><TableCell>{formatInstanceDateTime(email.receivedAt)}</TableCell><TableCell>{email.fromEmail || "-"}</TableCell><TableCell>{email.subject || "(No subject)"}</TableCell><TableCell><Chip size="small" label={email.status} /></TableCell></TableRow>)}
            </TableBody></Table>
          ) : <Alert severity="info">No unmapped emails. Future mailbox sync results that cannot be matched will appear here.</Alert>}

          <Typography variant="subtitle2" fontWeight={700}>Future placeholders</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
            <FutureFeatureCard title="Provider adapters" description="Microsoft Graph remains primary for Microsoft 365, with secure IMAP available for non-Microsoft mailboxes." items={["Microsoft 365 via Graph", "Secure IMAP for app-password providers", "Manual/import remains future"]} />
            <FutureFeatureCard title="Delivery remains separate" description="Outbound delivery is not part of mailbox sync." items={["No SMTP sending", "No POP3", "No automatic customer replies"]} />
          </Box>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <Button variant="contained" onClick={saveRoutingSettings} disabled={routingSaving || emailLoading}>Save Email Routing Settings</Button>
            <Typography variant="body2" color="text.secondary">External delivery is disabled until a future implementation explicitly enables it.</Typography>
          </Stack>
        </Stack>
      </SettingsGroup>

      <AccountabilitySettingsDialog open={accountabilityOpen} instanceUniqueId={instanceUniqueId} canEdit={canManage} onClose={() => setAccountabilityOpen(false)} onSaved={() => setAccountabilityOpen(false)} />
    </Stack>
  );
}
